import type { Axios } from 'axios'
import { z } from 'zod'

const TargetSchema = z.object({
  document_id: z.string().min(1),
  page_id: z.string().min(1),
  page_number: z.number().int().positive(),
  drawing_number: z.string(),
  sheet_number: z.string().nullable(),
  revision_code: z.string().nullable(),
  title: z.string(),
  evidence: z.string(),
  match_basis: z.enum(['drawing_identity', 'contextual_candidate']),
})

const ResponseSchema = z.object({
  status: z.enum(['resolved', 'ambiguous', 'not_found']),
  targets: z.array(TargetSchema).max(3),
  pipeline_config_id: z.string(),
  lookup_id: z.string(),
})

export type ConnectorLookupTarget = z.infer<typeof TargetSchema>
export type ConnectorLookupResponse = z.infer<typeof ResponseSchema>
export type ConnectorOccurrence = {
  raw_text: string
  context_text: string
  source_page_number: number
  source_bbox?: [number, number, number, number]
}
export type ConnectorLookupRequest = ConnectorOccurrence & {
  site_id: string
  pipeline_config_id: string
  preview_mode: boolean
  source_document_id: string
}

export async function fetchConnectorLookup(
  axios: Pick<Axios, 'post'>,
  backendUrl: string,
  request: ConnectorLookupRequest,
  signal: AbortSignal
): Promise<ConnectorLookupResponse> {
  const response = await axios.post(
    `${backendUrl}/api/v2/documents/connectors/resolve`,
    request,
    { signal, timeout: 4500 }
  )
  const parsed = ResponseSchema.parse(response.data)
  if (parsed.pipeline_config_id !== request.pipeline_config_id) {
    throw new Error('Document configuration changed. Reload and retry.')
  }
  if (parsed.status === 'resolved' && parsed.targets.length !== 1) {
    throw new Error('Connector lookup returned an invalid destination.')
  }
  return parsed
}

/** Owned by one viewer/auth scope; never shares cached document access across users. */
export function createConnectorLookupSession(
  fetch: (
    request: ConnectorLookupRequest,
    signal: AbortSignal
  ) => Promise<ConnectorLookupResponse>,
  scope: string = ''
) {
  const cache = new Map<
    string,
    { value: ConnectorLookupResponse; expires: number }
  >()
  let active:
    | {
        key: string
        controller: AbortController
        promise: Promise<ConnectorLookupResponse | undefined>
      }
    | undefined
  let generation = 0

  function cancel() {
    generation += 1
    active?.controller.abort()
    active = undefined
  }

  function run(
    request: ConnectorLookupRequest
  ): Promise<ConnectorLookupResponse | undefined> {
    const key = `${scope}:${JSON.stringify(request)}`
    if (active?.key === key) return active.promise
    cancel()
    const cached = cache.get(key)
    if (cached && cached.expires > Date.now())
      return Promise.resolve(cached.value)
    const controller = new AbortController()
    const currentGeneration = generation
    const promise = fetch(request, controller.signal)
      .then((value) => {
        if (generation !== currentGeneration) return undefined
        if (cache.size >= 50) cache.clear()
        cache.set(key, { value, expires: Date.now() + 60_000 })
        return value
      })
      .catch((error: Error) => {
        if (generation !== currentGeneration) return undefined
        throw error
      })
      .finally(() => {
        if (generation === currentGeneration) active = undefined
      })
    active = { key, controller, promise }
    return promise
  }

  return { run, cancel }
}
